import { Alert, Snackbar } from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { useThemeContext } from '../../../contexts/BaseThemeContextProvider';

/*
How to use it

1. Single message
<SnackBarAlert
    isOpen={savedUserAttributes} 
    handleCloseMessage={handleCloseMessage}
    message={"Profile updated successfully"}
    severityType={"success"} // if not provided uses "success"
    position={{ vertical: 'top', horizontal: 'center' }} // if not provided uses {vertical: 'top', horizontal: 'center'
   />

2. Multiple messages
 This could easily be used to add messages based on different conditions and can be combined with the custom hook
  called useSnackBarAlertMessages a good example can be found in Home.js component

<SnackBarAlert
    messages={messages}
    handleCloseMessage={handleCloseMessage} />

 as you noticed severityType is not provided because is it included on each message in the case that you need to render
 different type of messages, success, warning, error, etc so it is more flexible
*/

const SnackBarAlert = ({ messages, message, isOpen, handleCloseMessage, severityType, position, autoHideDuration }) => {
  const isArrayBased = Array.isArray(messages);

  const handleCloseArrayMessage = (index) => {
    if (typeof handleCloseMessage === 'function') {
      handleCloseMessage(index);
    }
  };

  const handleCloseSingleMessage = () => {
    if (typeof handleCloseMessage === 'function') {
      handleCloseMessage();
    }
  };

  useEffect(() => {
    if (messages?.length > 0) {
      const timer = setTimeout(() => {
        // we make sure the very first added message is removed
        // in the case of multiple messages
        handleCloseMessage(0);
      }, autoHideDuration);
      return () => clearTimeout(timer);
    }
  }, [messages, handleCloseMessage, autoHideDuration]);

  const { theme } = useThemeContext();
  const severityTypeStyles = theme.custom.snackBarAlert.severityTypeStyles;
  const severityIcons = {
    success: <CheckCircleOutlineIcon fontSize="inherit"
               sx={{
                 color: theme.custom.snackBarAlert.severityTypeStyles[severityType].color,
                 stroke: theme.custom.snackBarAlert.severityTypeStyles[severityType].color
               }}
             />,
    error: <ErrorOutlineIcon fontSize="inherit"
               sx={{
                 color: theme.custom.snackBarAlert.severityTypeStyles[severityType].color,
                 stroke: theme.custom.snackBarAlert.severityTypeStyles[severityType].color
               }}
             />,
    warning: <WarningAmberIcon fontSize="inherit"
               sx={{
                 color: theme.custom.snackBarAlert.severityTypeStyles[severityType].color,
                 stroke: theme.custom.snackBarAlert.severityTypeStyles[severityType].color
               }}
             />,
    info: <InfoOutlinedIcon fontSize="inherit"
               sx={{
                 color: theme.custom.snackBarAlert.severityTypeStyles[severityType].color,
                 stroke: theme.custom.snackBarAlert.severityTypeStyles[severityType].color
               }}
             />,
  };

  if (isArrayBased) {
    return (
      <>
        {messages.map(({ message, severityType }, index) => (
          <Snackbar
            key={index}
            open={Boolean(message)}
            anchorOrigin={position}
            autoHideDuration={autoHideDuration}
            onClose={() => handleCloseArrayMessage(index)}
          >
            <Alert
              onClose={() => handleCloseArrayMessage(index)}
              severity={severityType || 'success'}
              sx={{
                width: '100%',
                ...severityTypeStyles[severityType],
              }}
              icon={severityIcons[severityType]}
            >
              {message}
            </Alert>
          </Snackbar>
        ))}
      </>
    );
  }

  return (
    <Snackbar
      open={Boolean(isOpen)}
      anchorOrigin={position}
      autoHideDuration={autoHideDuration}
      onClose={handleCloseSingleMessage}
    >
      <Alert onClose={handleCloseSingleMessage} severity={severityType}
        sx={{
          width: '100%',
          ...severityTypeStyles[severityType],
        }}>
        {message}
      </Alert>
    </Snackbar>
  );
};

SnackBarAlert.propTypes = {
  isOpen: PropTypes.any,
  handleCloseMessage: PropTypes.func.isRequired,
  message: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      message: PropTypes.string,
      severityType: PropTypes.oneOf(['success', 'info', 'warning', 'error']),
    }),
  ]),
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      message: PropTypes.string,
      severityType: PropTypes.oneOf(['success', 'info', 'warning', 'error']),
    }),
  ),
  autoHideDuration: PropTypes.number,
  severityType: PropTypes.oneOf(['success', 'info', 'warning', 'error']),
  position: PropTypes.shape({
    vertical: PropTypes.oneOf(['top', 'bottom']),
    horizontal: PropTypes.oneOf(['left', 'center', 'right']),
  }),
};

SnackBarAlert.defaultProps = {
  autoHideDuration: 6000,
  severityType: 'success',
  position: { vertical: 'top', horizontal: 'center' },
};

export default SnackBarAlert;
